import { AxiosError } from "axios";
import { Suspense, lazy, useContext, useEffect, useMemo } from "react";
import { twMerge } from "tailwind-merge";
import { useBoolean } from "usehooks-ts";

import { AssessmentGradingFormProvider } from "../../../providers/AssessmentGradingFormProvider";

import { switchOrg } from "api/api-server-actions";
import { serverGetPageAssessmentStatuses } from "api/api-server-data";
import { serverFetchLatestPublications } from "api/api-server-publishing";
import { getSectionUserData } from "api/api-worksheets";
import { LoadingScreen } from "components/LoadingScreen";
import { MobileRoutingOverlay } from "components/MobileRoutingOverlay";
import { useAITool } from "components/ai/hooks/use-ai-tool";
import { withActiveAv } from "components/av/AvSubsystemLoader";
import { MainFooter } from "components/footer/MainFooter";
import { MainHeader } from "components/header/MainHeader";
import { MobileMainHeader } from "components/header/MobileMainHeader";
import { isProjectorView } from "components/materials/presentation/projector/projector-messaging";
import { SidebarOpenContainer } from "components/materials/sidebar/SidebarOpenContainer";
import { t } from "i18n/i18n";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { BypassReleaseWarningContext } from "providers/BypassReleaseWarningProvider";
import { PubsubWebsocketContext } from "providers/PubsubWebsocketProvider";
import { ShareDBProvider } from "providers/ShareDBProvider";
import { useCapabilities } from "store/capability-hooks";
import { store, storeApi, useAppDispatch, useAppSelector } from "store/index";
import { clearPubsubAPICache } from "store/pubsub-slice";
import {
  selectCurrentClassUser,
  selectCurrentSectionId,
  selectCurrentSectionRole,
  selectCurrentUserId,
  selectIsClassSessionVirtual,
  selectIsRecordingAgent,
  selectOrgIdForSectionId,
} from "store/selectors";
import {
  selectClassStateLastUpdatedAt,
  selectIsObservingVirtualClass,
  selectStableIsMainAvTab,
} from "store/selectors/pubsub-virtual-class-state";
import { attendanceStateActions } from "store/slices/attendance";
import { computedStateActions } from "store/slices/computed";
import { PubsubVirtualClassStateActions } from "store/slices/pubsub-virtual-class-state";
import { viewStateActions } from "store/slices/view";
import { trackUserProperty } from "utils/amplitude";
import { reportApiFailure } from "utils/api-errors";
import { getOrgIdFromCookie, setLastSectionIdCookie } from "utils/auth";
import { useAv, useAvPubsubMessaging } from "utils/av";
import { isTestEnv } from "utils/env";
import { registerPageAssessmentStatusPubsubListener } from "utils/listeners/assessment-status";
import { logInfoWithDebugFlag, rollbarAndLogWarning } from "utils/logger";
import { isTemplateSection } from "utils/publishing";
import { Outlet, useParams } from "utils/routing";
import { isAVEnabled } from "utils/waffle-flags";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

const LazyParticipantBar = withActiveAv(
  () => import("components/av/ParticipantBar"),
  t("av.loading_users"),
  true,
);

const AvContextProvider = lazy(() => import("components/av/AvProvider"));

export default function Layout() {
  const isMobile = !useIsMdOrLarger();

  // Ensure this component refreshes when we navigate between section routes
  const { sectionId } = useParams();
  // It's important we lazy-load any entry to the AV subsystem, as it uses many heavy dependencies
  // and is only rarely enabled.
  const orgIdForSectionId = useAppSelector((s) => selectOrgIdForSectionId(s, sectionId));
  const orgIdFromCookie = getOrgIdFromCookie();
  const userId = useAppSelector(selectCurrentUserId);
  const { canAuthorSection } = useCapabilities();
  const dispatch = useAppDispatch();
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const classUser = useAppSelector(selectCurrentClassUser);
  const isProjectorPath = isProjectorView();

  const { pubsubStatus } = useContext(PubsubWebsocketContext);

  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();

  const stableIsMainAvTab = useAppSelector(selectStableIsMainAvTab);
  const hasVirtualClassPubsubUpdate = useAppSelector(selectClassStateLastUpdatedAt) !== null;

  useAITool("read_page");

  const sectionRole = useAppSelector(selectCurrentSectionRole);
  useEffect(() => {
    if (sectionRole) {
      trackUserProperty("courseRole", sectionRole);
    }
  }, [sectionRole]);

  useEffect(() => {
    return registerPageAssessmentStatusPubsubListener(store);
  }, []);

  const isClassSessionVirtual = useAppSelector(selectIsClassSessionVirtual);

  const isRecordingAgent = useAppSelector(selectIsRecordingAgent);
  useEffect(() => {
    if (classUser?.id && isRecordingAgent) {
      storeApi.class_users.partial_update({ id: classUser?.id, is_in_class_session: true });
    }
  }, [isRecordingAgent, classUser?.id]);

  const { isInVirtualClassOrObserving } = useAv();
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);
  const isDarkMode = isInVirtualClassOrObserving;

  // We consider for this component that a user is in a virtual class if the virtual class exists
  // and the user is either participating or performing an AV check to get in.
  const isUserInVirtualClass =
    !isProjectorPath &&
    isClassSessionVirtual &&
    (classUser?.is_in_class_session || classUser?.is_performing_av_check);

  // Virtual class setup: instructor is performing AV check
  const isVirtualClassSetup = useMemo(
    () => canAuthorSection && classUser?.is_performing_av_check,
    [canAuthorSection, classUser?.is_performing_av_check],
  );

  // We should consider starting the AV subsystem if the user is in a virtual class session or setup.
  const isVirtualSessionOrSetup = isAVEnabled() && (isUserInVirtualClass || isVirtualClassSetup);

  // We should request the AV state if the pubsub websocket is connected to make sure the join message is sent.
  const shouldRequestAvState = pubsubStatus === "connected" && isVirtualSessionOrSetup;

  const shouldStartAv = stableIsMainAvTab || isObservingClass;

  // This useEffect ensures we log the state of shouldStartAv whenever it changes,
  // which is useful for debugging the AV subsystem mounting and unmounting.
  useEffect(() => {
    if (!isTestEnv) {
      logInfoWithDebugFlag(
        "debug_virtual_class",
        "[Layout] shouldStartAv changed:",
        shouldStartAv,
        {
          shouldRequestAvState,
          hasVirtualClassPubsubUpdate,
          stableIsMainAvTab,
          pubsubStatus,
          isProjectorPath,
          avEnabled: isAVEnabled(),
          isUserInVirtualClass,
          isVirtualClassSetup,
          classUser: {
            is_in_class_session: classUser?.is_in_class_session,
            is_performing_av_check: classUser?.is_performing_av_check,
          },
        },
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldStartAv]);

  useEffect(() => {
    if (isObservingClass) return;
    if (shouldRequestAvState && !hasVirtualClassPubsubUpdate) {
      logInfoWithDebugFlag(
        "debug_virtual_class",
        "[Layout] Virtual Class. Sending join message, force: false",
      );
      sendPubsubVirtualClassStateMessage({
        action: "join",
        force: isRecordingAgent || false,
      });
    } else if (hasVirtualClassPubsubUpdate && !shouldRequestAvState) {
      if (stableIsMainAvTab) {
        logInfoWithDebugFlag(
          "debug_virtual_class",
          "[Layout] Virtual Class. Sending leave message",
        );
        sendPubsubVirtualClassStateMessage({ action: "leave" });
      }
      logInfoWithDebugFlag(
        "debug_virtual_class",
        "[Layout] Clearing virtual class state not in AV tab nor observing",
      );
      dispatch(PubsubVirtualClassStateActions.clearVirtualClassState());
    }
  }, [
    shouldRequestAvState,
    isObservingClass,
    hasVirtualClassPubsubUpdate,
    stableIsMainAvTab,
    sendPubsubVirtualClassStateMessage,
    dispatch,
    sectionId,
    isRecordingAgent,
  ]);

  const needsOrgSwitch = orgIdForSectionId && orgIdForSectionId !== orgIdFromCookie;

  useEffect(() => {
    if (!userId || !sectionId) {
      return;
    }
    // If we detect that we need to switch org, we skip the course retrieval
    if (needsOrgSwitch) {
      return;
    }
    storeApi.sections.retrieve(sectionId, { skipToast: true }).catch((error) => {
      if (
        error instanceof AxiosError &&
        error.response?.status &&
        [404, 403, 401].includes(error.response.status)
      ) {
        rollbarAndLogWarning("Section not found, rerouting to index", error);
        window.location.href = "/";
      } else {
        reportApiFailure("Failed to load section", error);
      }
    });
  }, [sectionId, userId, needsOrgSwitch]);

  // If we try to load a course URL that doesn't exist, but it _does_ exist in another org
  // that the user has access to, we switch org and reload the page.
  useEffect(() => {
    async function switchOrgIfNeeded() {
      if (!needsOrgSwitch || !userId) {
        return;
      }

      try {
        await switchOrg(orgIdForSectionId);
        window.location.reload();
      } catch (error) {
        // We don't show the error the user, because it's too technical. They will just see a 404.
        reportApiFailure("Failed to auto-switch org", error);
      }
    }

    switchOrgIfNeeded();
  }, [needsOrgSwitch, orgIdForSectionId, userId]);

  // Whenever we navigate to a course, we want to remember which course it was, so that accessing
  // the app root will preferably load the same course again.
  useEffect(() => {
    if (sectionId) setLastSectionIdCookie(sectionId);
  }, [sectionId]);

  useEffect(() => {
    const loadSectionData = async () => {
      if (!userId || !sectionId) {
        return;
      }

      // List responses are cached by query params only; the same section_id can return different
      // payloads after role/capability changes. Clear before refetch so we never reuse stale lists
      // (e.g. pages for students vs staff). Runs whenever this loader effect re-fires (section, user, or canAuthorSection).
      clearPubsubAPICache();
      dispatch(viewStateActions.setCriticalSectionDataPending(true));
      try {
        storeApi.courses.list();
        storeApi.terms.list();
        storeApi.class_sessions.list({ section_id: sectionId });
        storeApi.topics.list({ section_id: sectionId });
        storeApi.activities.list({ section_id: sectionId });
        storeApi.pages.list({ section_id: sectionId });
        storeApi.page_access_data.list({ section_id: sectionId });
        storeApi.recording_sessions.list({ section_id: sectionId });
        storeApi.outcomes.list();
        storeApi.section_outcomes.list({ section_id: sectionId });
        storeApi.page_outcomes.list({ section_id: sectionId });
        const [sections, sectionUsers, statuses] = await Promise.all([
          storeApi.sections.list(),
          storeApi.section_users.list({ section_id: sectionId }),
          serverGetPageAssessmentStatuses({ sectionId }),
        ]);
        // Dispatch statuses immediately as each batch completes
        dispatch(computedStateActions.savePageAssessmentStatuses(statuses));

        // Get or create class user if not exists
        const classUsers = await storeApi.class_users.list({ section_id: sectionId });

        const currentSectionUserId = sectionUsers.find(
          (sectionUser) => sectionUser.user_id === userId,
        )?.id;

        const currentSection = sections.find((s) => s.id === sectionId);
        let isTemplate = false;

        if (currentSection) {
          storeApi.course_users.list({ course_id: currentSection?.course_id });
          isTemplate = isTemplateSection(currentSection);
          if (canAuthorSection && isTemplate) {
            serverFetchLatestPublications(sectionId);
          }
        }

        // Even though ClassUsers reference user_id (not section_user_id), we need to ensure the section user exists
        // before creating a class user. Upon create, the server calls get_section_user() to sync breakout groups;
        // without a section user that lookup would fail.
        if (!currentSectionUserId) {
          logInfoWithDebugFlag(
            "debug_virtual_class",
            "Current sectionUser not found: not creating class user",
            { sectionId, userId },
          );

          return;
        }

        const currentClassUser = classUsers.find((cu) => cu.user_id === userId);
        if (!currentClassUser && !isTemplate) {
          logInfoWithDebugFlag("debug_virtual_class", "Creating class user", { sectionId, userId });
          await storeApi.class_users.create(
            {
              section_id: sectionId,
              user_id: userId,
            },
            {
              // TODO: Make class-user creation idempotent so duplicate creates succeed while
              // genuine failures can continue to show a user-facing error toast.
              // Section hydration can race with another successful create. Keep the losing
              // background request from showing a misleading user-facing error toast.
              skipToast: true,
            },
          );
        }
      } finally {
        dispatch(viewStateActions.setCriticalSectionDataPending(false));
      }
    };

    // Once we have the course and know it exists, we can start loading all related data
    if (sectionId && currentSectionId === sectionId && !!userId) {
      loadSectionData();
    }
  }, [currentSectionId, sectionId, dispatch, userId, canAuthorSection]);

  useEffect(() => {
    const getSectionActivityData = async () => {
      if (!sectionId) return;
      const activityData = await getSectionUserData(sectionId);
      dispatch(attendanceStateActions.updateAnalytics(activityData));
    };

    if (userId && sectionId) {
      getSectionActivityData();
    }
  }, [sectionId, dispatch, userId]);

  const { value: showWarning, setTrue: resetWarning, setFalse: bypassWarning } = useBoolean(true);
  const bypassReleaseWarningValue = useMemo(
    () => ({ showWarning, resetWarning, bypassWarning }),
    [showWarning, resetWarning, bypassWarning],
  );

  const layoutContent = (
    <div className={twMerge("flex h-full flex-col", isDarkMode && "bg-black-tint-15")}>
      {!isProjectorPath && (isMobile ? <MobileMainHeader /> : <MainHeader />)}
      {shouldStartAv && <LazyParticipantBar />}
      <SidebarOpenContainer>
        <div className={twMerge("relative h-full max-w-full grow", isDarkMode && "px-3")}>
          <ErrorBoundary>
            <MobileRoutingOverlay />
            <Outlet />
          </ErrorBoundary>
        </div>
      </SidebarOpenContainer>
      {!isProjectorPath && <MainFooter />}
    </div>
  );

  return (
    <ShareDBProvider>
      <BypassReleaseWarningContext.Provider value={bypassReleaseWarningValue}>
        <AssessmentGradingFormProvider>
          {shouldStartAv ? (
            <Suspense fallback={<LoadingScreen>{t("av.loading_av_subsystem")}</LoadingScreen>}>
              <AvContextProvider>{layoutContent}</AvContextProvider>
            </Suspense>
          ) : (
            layoutContent
          )}
        </AssessmentGradingFormProvider>
      </BypassReleaseWarningContext.Provider>
    </ShareDBProvider>
  );
}
